import React, { forwardRef, useState } from "react";
import { Box, Typography } from "@mui/material";
import { ImageNotSupported } from "@mui/icons-material";
import { PriceListProduct } from "../../../services/toolsService";
import { API_CONFIG } from "../../../config/api";
import { formatCurrencyBDT } from "../../../utils/currency";
import { RECEIPT_CONFIG } from "../../../config/receipt";

interface ProductCollageProps {
    products: PriceListProduct[];
}

// A collage is meant to be viewed on a phone, so it stays a fixed,
// moderate width and grows in rows — the opposite of the single
// ProductCard, which grows to whatever height its one photo needs.
const COLLAGE_WIDTH = 480;

// Past this many tiles a collage stops being legible on a phone screen
// regardless of layout — the "download every card separately" flow
// (see PriceListGenerator's Individual mode) already covers a large
// selection, so the collage caps rather than shrinking tiles indefinitely.
export const MAX_COLLAGE_PRODUCTS = 12;

// A handful of columns, chosen from the selection size, biased toward a
// portrait *overall* shape rather than a compact square grid — most
// people will open this on a phone, where a tall image reads naturally
// and a wide one gets awkwardly shrunk. Never more than 3 columns: each
// tile is already a square photo + a name underneath (portrait on its
// own), so even 3 columns keeps the whole collage taller than it is wide
// once there's more than one row.
export function getCollageColumnCount(productCount: number): number {
    if (productCount <= 2) return 1;
    if (productCount <= 8) return 2;
    return 3;
}

const CollageTile: React.FC<{ product: PriceListProduct }> = ({ product }) => {
    const [imageFailed, setImageFailed] = useState(false);
    const imageUrl = `${API_CONFIG.BASE_URL}/products/${product.product_id}/card-image`;

    return (
        <Box sx={{ minWidth: 0 }}>
            <Box
                sx={{
                    position: "relative",
                    width: "100%",
                    aspectRatio: "1 / 1",
                    bgcolor: "grey.100",
                    borderRadius: 1.5,
                    overflow: "hidden",
                }}
            >
                {/* Uniform square crop, unlike the single ProductCard's
                    natural-ratio photo — a collage's whole appeal is the
                    aligned grid, so every tile has to share one shape
                    regardless of each photo's own proportions. */}
                {imageFailed ? (
                    <Box
                        sx={{
                            width: "100%",
                            height: "100%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                        }}
                    >
                        <ImageNotSupported sx={{ fontSize: 28, color: "grey.400" }} />
                    </Box>
                ) : (
                    <img
                        src={imageUrl}
                        alt={product.name}
                        crossOrigin="anonymous"
                        onError={() => setImageFailed(true)}
                        style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                    />
                )}

                <Box
                    sx={{
                        position: "absolute",
                        right: 4,
                        bottom: 4,
                        bgcolor: "#fff",
                        borderRadius: 3,
                        px: 0.75,
                        py: 0.25,
                        boxShadow: "0 1px 3px rgba(0,0,0,0.35)",
                    }}
                >
                    <Typography
                        fontWeight={800}
                        color="primary.main"
                        sx={{ fontSize: "0.7rem", lineHeight: 1.2, whiteSpace: "nowrap" }}
                    >
                        {formatCurrencyBDT(product.price)}
                    </Typography>
                </Box>
            </Box>

            <Typography
                variant="caption"
                fontWeight={700}
                sx={{
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                    lineHeight: 1.3,
                    mt: 0.5,
                }}
            >
                {product.name}
            </Typography>
        </Box>
    );
};

/**
 * Multiple products laid out as one shareable image — a grid of small
 * photo+price tiles under a single shared shop-branding header, rather
 * than each product carrying its own logo/footer like the single
 * ProductCard does (repeating that per-tile at this size would just be
 * noise). Rasterized the same way as ProductCard (see exportCardImage.ts):
 * pure display component, ref forwarded straight to the outer element for
 * html2canvas to capture.
 */
export const ProductCollage = forwardRef<HTMLDivElement, ProductCollageProps>(
    ({ products }, ref) => {
        const shown = products.slice(0, MAX_COLLAGE_PRODUCTS);
        const columns = getCollageColumnCount(shown.length);

        return (
            <Box
                ref={ref}
                sx={{
                    width: COLLAGE_WIDTH,
                    bgcolor: "#fff",
                    borderRadius: 2,
                    overflow: "hidden",
                    border: "1px solid",
                    borderColor: "divider",
                    fontFamily: "system-ui, -apple-system, sans-serif",
                }}
            >
                <Box
                    sx={{
                        bgcolor: "primary.main",
                        color: "#fff",
                        py: 1.5,
                        px: 2,
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                    }}
                >
                    <Box
                        sx={{
                            width: 36,
                            height: 36,
                            borderRadius: "50%",
                            bgcolor: "rgba(255,255,255,0.15)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                        }}
                    >
                        <img
                            src="/images/logo192.png"
                            alt=""
                            style={{ width: 26, height: 26, objectFit: "contain" }}
                        />
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                        <Typography variant="subtitle1" fontWeight={800} sx={{ color: "#fff", lineHeight: 1.2 }}>
                            {RECEIPT_CONFIG.SHOP_INFO.NAME}
                        </Typography>
                        <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.85)" }}>
                            {RECEIPT_CONFIG.SHOP_INFO.PHONE}
                        </Typography>
                    </Box>
                </Box>

                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: `repeat(${columns}, 1fr)`,
                        gap: 1.5,
                        p: 1.5,
                    }}
                >
                    {shown.map((product) => (
                        <CollageTile key={product.product_id} product={product} />
                    ))}
                </Box>
            </Box>
        );
    },
);

ProductCollage.displayName = "ProductCollage";
